export interface OnboardingControlsTexture {
  key: string;
  frame: string | number;
}

export const OnboardingStep = {
  Accelerate: 'accelerate',
  PrepChangeLane: 'prepChangeLane',
  ChangeLane: 'changeLane'
} as const;
export type OnboardingStepKeys = keyof typeof OnboardingStep;
export type OnboardingStepValues = typeof OnboardingStep[OnboardingStepKeys];

/* START OF COMPILED CODE */

import Phaser from "phaser";
/* START-USER-IMPORTS */
import { PlayerController } from '../PlayerController';
import { Vehicle } from '../Vehicle';
import { RunManager } from "../level/RunManager";
/* END-USER-IMPORTS */

export default class OnboardingControls extends Phaser.GameObjects.Container {

  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 182, y ?? 59.5);

    // keySpacebar
    const keySpacebar = scene.add.image(0, 0, "common", "key-spacebar.png");
    keySpacebar.scaleX = 0.4;
    keySpacebar.scaleY = 0.4;
    this.add(keySpacebar);

    // keyDown
    const keyDown = scene.add.image(0, -1, "common", "key-down.png");
    keyDown.scaleX = 0.5;
    keyDown.scaleY = 0.5;
    this.add(keyDown);

    // keyUp
    const keyUp = scene.add.image(0, -28, "common", "key-up.png");
    keyUp.scaleX = 0.5;
    keyUp.scaleY = 0.5;
    this.add(keyUp);

    // handPoint
    const handPoint = scene.add.image(0, 0, "common", "hand-point.png");
    handPoint.scaleX = 0.5;
    handPoint.scaleY = 0.5;
    handPoint.visible = false;
    this.add(handPoint);

    this.keySpacebar = keySpacebar;
    this.keyDown = keyDown;
    this.keyUp = keyUp;
    this.handPoint = handPoint;

    /* START-USER-CTR-CODE */
    this.isActive = false;
    this.isAnimating = false;
    this.isMobile = !this.scene.sys.game.device.os.desktop;
    this.setVisible(false);

    this.currentOnboardingStep = OnboardingStep.Accelerate;
    this.isShowingPressed = false;
    this.isTweening = false;
    this.isTweenAtStart = false;
    this.alternateTime = 1000;
    this.alternateTimer = 0;
    this.stepCompleteTimer = 0;

    this.on(Phaser.GameObjects.Events.DESTROY, () => {
      this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
      this.scene.events.off(RunManager.events.RunOver, this.onRunEnd, this);
    });

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.scene.events.once(RunManager.events.RunOver, this.onRunEnd, this);
    /* END-USER-CTR-CODE */

    // custom definition props
    this.keySpacebarTexture = {"key":"common","frame":"key-spacebar-press.png"};
    this.keySpacebarPressedTexture = {"key":"common","frame":"key-spacebar.png"};
    this.keyUpTexture = {"key":"common","frame":"key-up-press.png"};
    this.keyUpPressedTexture = {"key":"common","frame":"key-up.png"};
    this.keyDownTexture = {"key":"common","frame":"key-down-press.png"};
    this.keyDownPressedTexture = {"key":"common","frame":"key-down.png"};
    this.handTexture = {"key":"common","frame":"hand-point.png"};
    this.handPressedTexture = {"key":"common","frame":"hand-press.png"};
  }

  private keySpacebar: Phaser.GameObjects.Image;
  private keyDown: Phaser.GameObjects.Image;
  private keyUp: Phaser.GameObjects.Image;
  private handPoint: Phaser.GameObjects.Image;
  public vehicleOffsetY: number = -220;
  public handAcceleratePositionX: number = 1400;
  public handAcceleratePositionY: number = 20;
  public acceleratePressTime: number = 1000;
  public changeLaneDisplayTime: number = 2000;
  public handChangeLanePositionX: number = 1400;
  public handChangeLaneStartY: number = 404;
  public handChangeLaneEndY: number = 700;

  /* START-USER-CODE */
  public keySpacebarTexture: OnboardingControlsTexture;
  public keySpacebarPressedTexture: OnboardingControlsTexture;
  public keyUpTexture: OnboardingControlsTexture;
  public keyUpPressedTexture: OnboardingControlsTexture;
  public keyDownTexture: OnboardingControlsTexture;
  public keyDownPressedTexture: OnboardingControlsTexture;
  public handTexture: OnboardingControlsTexture;
  public handPressedTexture: OnboardingControlsTexture;

  protected isActive: boolean;
  protected isAnimating: boolean;
  protected isMobile: boolean;
  protected controller: PlayerController | undefined;
  protected vehicle: Vehicle | undefined;

  protected currentOnboardingStep: OnboardingStepValues;
  protected isShowingPressed: boolean;
  protected isTweening: boolean;
  protected isTweenAtStart: boolean;
  protected alternateTime: number;
  protected alternateTimer: number;
  protected stepCompleteTimer: number;

  public setTarget(controller: PlayerController, vehicle: Vehicle) {
    this.controller = controller;
    this.vehicle = vehicle;
  }

  public start() {
    this.isActive = true;
    this.isMobile = !this.scene.sys.game.device.os.desktop;
    this.currentOnboardingStep = OnboardingStep.Accelerate;
    this.currentOnboardingStep = OnboardingStep.PrepChangeLane;

    this.reset();
    this.setVisible(true);

    // this.startStepAccelerate();
    this.startStepChangeLane();
  }

  public stop() {
    this.isActive = false;
    this.setVisible(false);
  }

  public reset() {
    this.keySpacebar.setVisible(false);
    this.keyUp.setVisible(false);
    this.keyDown.setVisible(false);
    this.handPoint.setVisible(false);

    if (this.isMobile) this.setScrollFactor(0, 0);
    else this.setScrollFactor(1, 1);
  }

  public update(time: number, delta: number) {
    if (!this.isActive) return;

    // Move with vehicle if not mobile.
    if (!this.isMobile) {
      this.x = this.vehicle?.x ?? 0;
      this.y = (this.vehicle?.y ?? 0) + this.vehicleOffsetY;
    } else if (this.isMobile && this.currentOnboardingStep == OnboardingStep.Accelerate) {
      this.handPoint.y = (this.vehicle?.y ?? 0) + 384 + this.handAcceleratePositionY;
    }

    if (this.isAnimating) {
      if (this.currentOnboardingStep == OnboardingStep.Accelerate) {
        if (this.vehicle?.IsAccelerating) this.stepCompleteTimer += delta;
        else this.stepCompleteTimer = 0;

        if (this.stepCompleteTimer >= this.acceleratePressTime) {
          if (this.currentOnboardingStep == OnboardingStep.Accelerate) {
            this.endStepAccelerate();
            if (this.isMobile) this.currentOnboardingStep = OnboardingStep.PrepChangeLane;
            else this.currentOnboardingStep = OnboardingStep.ChangeLane;
            this.startStepChangeLane();
          }
        }
      } else if (this.currentOnboardingStep == OnboardingStep.ChangeLane && this.vehicle && this.vehicle.HasChangedLaneOnce) {
        this.stepCompleteTimer += delta;

        if (this.stepCompleteTimer >= this.changeLaneDisplayTime) {
          this.endStepChangeLane();
          this.stop();
        }
      }

      this.alternateTimer -= delta;

      if (this.alternateTimer <= 0) {
        this.alternateTimer = this.alternateTime;

        if (this.currentOnboardingStep == OnboardingStep.Accelerate) {
          if (this.isShowingPressed) this.showAccelerateUp();
          else this.showAccelerateDown();
        } else if (!this.isMobile && this.currentOnboardingStep == OnboardingStep.ChangeLane) {
          if (this.isShowingPressed) this.showUpPressed();
          else this.showDownPressed();
        }

        this.isShowingPressed = !this.isShowingPressed;
      }
    }

    if (this.currentOnboardingStep == OnboardingStep.ChangeLane && !this.isTweening) {
      if (this.isTweenAtStart) {
        this.isTweenAtStart = false;
        this.tweenHand(this.handChangeLaneEndY, 0);
      } else {
        this.isTweenAtStart = true;
        this.tweenHand(this.handChangeLaneStartY, 0);
      }
    }
  }

  protected startStepAccelerate() {
    if (this.isMobile) {
      this.handPoint.setVisible(true);
      this.handPoint.x = this.handAcceleratePositionX;
      this.handPoint.y = this.handAcceleratePositionY;
    } else {
      this.keySpacebar.setVisible(true);
    }

    this.isAnimating = true;
    this.isShowingPressed = false;
    this.alternateTimer = this.alternateTimer;
    this.stepCompleteTimer = 0;
  }

  protected endStepAccelerate() {
    this.keySpacebar.setVisible(false);
    this.handPoint.setVisible(false);

    this.isAnimating = false;
  }

  protected showAccelerateUp() {
    if (this.isMobile) {
      this.handPoint.setFrame(this.handTexture.frame);
    } else {
      this.keySpacebar.setFrame(this.keySpacebarTexture.frame);
    }
  }

  protected showAccelerateDown() {
    if (this.isMobile) {
      this.handPoint.setFrame(this.handPressedTexture.frame);
    } else {
      this.keySpacebar.setFrame(this.keySpacebarPressedTexture.frame);
    }
  }

  protected startStepChangeLane() {
    if (this.isMobile) {
      this.handPoint.setVisible(true);
      this.handPoint.setFrame(this.handTexture.frame);

      // Remove hand tween if already roughly in position.
      if (
        this.handPoint.y > this.handChangeLaneStartY - 5 &&
        this.handPoint.y < this.handChangeLaneStartY + 5 &&
        this.handPoint.x > this.handChangeLanePositionX - 5 &&
        this.handPoint.x < this.handChangeLanePositionX + 5
      ) {
        this.isTweenAtStart = true;
        this.handPoint.y = this.handChangeLaneStartY;
        this.handPoint.setVisible(true);
        this.handPoint.setFrame(this.handPressedTexture.frame);
        this.currentOnboardingStep = OnboardingStep.ChangeLane;
      } else {
        this.scene.add.tween({
          targets: this.handPoint,
          x: this.handAcceleratePositionX,
          y: this.handChangeLaneStartY,
          duration: 1000,
          ease: Phaser.Math.Easing.Cubic.InOut,
          delay: 0,
          onComplete: () => {
            this.isTweenAtStart = true;
            this.handPoint.setVisible(true);
            this.handPoint.setFrame(this.handPressedTexture.frame);
            this.currentOnboardingStep = OnboardingStep.ChangeLane;
          }
        });
      }
    } else {
      this.currentOnboardingStep = OnboardingStep.ChangeLane;
      this.keyUp.setVisible(true);
      this.keyDown.setVisible(true);
    }

    this.isAnimating = true;
    this.isShowingPressed = false;
    this.alternateTimer = this.alternateTimer;
    this.stepCompleteTimer = 0;
  }

  protected endStepChangeLane() {
    this.handPoint.setVisible(false);
    this.keyUp.setVisible(false);
    this.keyDown.setVisible(false);

    this.isAnimating = false;
  }

  protected showUpPressed() {
    this.keyUp.setFrame(this.keyUpPressedTexture.frame);
    this.keyDown.setFrame(this.keyDownTexture.frame);
  }

  protected showDownPressed() {
    this.keyDown.setFrame(this.keyDownPressedTexture.frame);
    this.keyUp.setFrame(this.keyUpTexture.frame);
  }

  protected tweenHand(targetY: number, delay: number) {
    this.isTweening = true;

    this.scene.add.tween({
      targets: this.handPoint,
      y: targetY,
      duration: 1000,
      ease: Phaser.Math.Easing.Cubic.InOut,
      delay: delay,
      onComplete: () => {
        this.isTweening = false;
      }
    });
  }

  protected onRunEnd() {
    this.stop();
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
